/**
 * Configuration Error Page Component
 *
 * Displays friendly error messages when environment configuration is invalid.
 * Shows specific validation errors and provides troubleshooting steps.
 *
 * Usage:
 *   <ConfigErrorPage error={configError} />
 */

import React from 'react';
import { ConfigValidationError, ConfigError } from '../config/errors';
import { Button, Card, Stack, useTheme, spacing } from '@fti/design-system';

interface ConfigErrorPageProps {
  /** Configuration error with validation details */
  error: ConfigValidationError | ConfigError | Error;
}

/**
 * Configuration error page component
 *
 * Renders a helpful error page when configuration validation fails.
 * Lists specific missing or invalid variables and provides fix instructions.
 *
 * @param props.error - Configuration error to display
 * @returns Error page with troubleshooting information
 */
export function ConfigErrorPage({ error }: ConfigErrorPageProps): JSX.Element {
  const isValidationError = error instanceof ConfigValidationError;
  const validationErrors = isValidationError ? error.errors : [];
  const { theme } = useTheme();

  // Theme-aware colors from design system
  const bgColor = theme.colors.background.default;
  const cardBg = theme.colors.background.paper;
  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const errorColor = theme.colors.semantic.error.default;
  const infoColor = theme.colors.semantic.info.default;
  const codeBgColor = theme.colors.background.elevated;

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: bgColor,
      padding: `${spacing.xl} ${spacing.md}`
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <Card variant="elevated" style={{ background: cardBg }}>
          <Card.Body>
            <Stack gap="lg">
              <h1 style={{ color: errorColor, marginTop: 0, fontSize: '28px' }}>
                Configuration Error
              </h1>

              <p style={{ color: mutedColor, fontSize: '16px', lineHeight: '1.6' }}>
                The application configuration is invalid or incomplete. Please fix the following issues:
              </p>

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div style={{
                  padding: spacing.md,
                  backgroundColor: `${errorColor}15`,
                  border: `1px solid ${errorColor}`,
                  borderRadius: '4px'
                }}>
                  <h2 style={{ marginTop: 0, fontSize: '18px', color: errorColor }}>
                    Validation Errors:
                  </h2>
                  <ul style={{ margin: `${spacing.sm} 0`, paddingLeft: spacing.md, color: mutedColor }}>
                    {validationErrors.map((err: string, idx: number) => (
                      <li key={idx} style={{ marginBottom: spacing.xs }}>
                        <code style={{
                          backgroundColor: codeBgColor,
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '14px',
                          color: textColor
                        }}>
                          {err}
                        </code>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Error Message */}
              <div style={{
                padding: spacing.md,
                backgroundColor: codeBgColor,
                borderRadius: '4px',
                fontSize: '14px'
              }}>
                <strong style={{ color: textColor }}>Error Details:</strong>
                <pre style={{
                  marginTop: spacing.sm,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: mutedColor
                }}>
                  {error.message}
                </pre>
              </div>

              {/* Troubleshooting Steps */}
              <div style={{
                padding: spacing.md,
                backgroundColor: `${infoColor}15`,
                border: `1px solid ${infoColor}`,
                borderRadius: '4px'
              }}>
                <h2 style={{
                  marginTop: 0,
                  fontSize: '18px',
                  color: infoColor
                }}>
                  How to Fix:
                </h2>
                <ol style={{
                  margin: `${spacing.sm} 0`,
                  paddingLeft: spacing.md,
                  color: mutedColor,
                  lineHeight: '1.8'
                }}>
                  <li>
                    In a deployed container, configuration comes from environment variables rendered
                    into <code style={{
                      backgroundColor: codeBgColor,
                      padding: '2px 6px',
                      borderRadius: '3px',
                      color: textColor
                    }}>window.__RUNTIME_CONFIG__</code> at startup. For local <code>vite dev</code>, it
                    comes from a <code style={{
                      backgroundColor: codeBgColor,
                      padding: '2px 6px',
                      borderRadius: '3px',
                      color: textColor
                    }}>.env</code> file (see <code>.env.example</code>).
                  </li>
                  <li>
                    Ensure <code style={{
                      backgroundColor: codeBgColor,
                      padding: '2px 6px',
                      borderRadius: '3px',
                      color: textColor
                    }}>APP_ENVIRONMENT</code> (dev fallback <code>VITE_APP_ENVIRONMENT</code>) is one of:
                    <code>local</code>, <code>dev</code>, or <code>prod</code>.
                  </li>
                  <li>
                    Ensure <code style={{
                      backgroundColor: codeBgColor,
                      padding: '2px 6px',
                      borderRadius: '3px',
                      color: textColor
                    }}>API_BASE_URLS</code> is a JSON object of named backends and{' '}
                    <code style={{
                      backgroundColor: codeBgColor,
                      padding: '2px 6px',
                      borderRadius: '3px',
                      color: textColor
                    }}>DEFAULT_API_KEY</code> names one of its keys.
                  </li>
                  <li>
                    For <code>dev</code>/<code>prod</code> with <code style={{
                      backgroundColor: codeBgColor,
                      padding: '2px 6px',
                      borderRadius: '3px',
                      color: textColor
                    }}>AUTH_MODE=oidc</code>, set <code>OIDC_AUTHORITY</code>,{' '}
                    <code>OIDC_CLIENT_ID</code>, and <code>OIDC_SCOPE</code> (must include{' '}
                    <code>openid</code>). <code>local</code> may use <code>AUTH_MODE=mock</code>.
                  </li>
                  <li>
                    After changing container environment variables, recreate the deployment/pod so the
                    entrypoint re-renders <code style={{
                      backgroundColor: codeBgColor,
                      padding: '2px 6px',
                      borderRadius: '3px',
                      color: textColor
                    }}>env.js</code>.
                  </li>
                </ol>
              </div>

              {/* Quick Start for Local Development */}
              <div style={{
                padding: spacing.md,
                backgroundColor: codeBgColor,
                borderRadius: '4px',
                fontSize: '14px'
              }}>
                <strong style={{ color: textColor }}>Quick Start (Local Development):</strong>
                <pre style={{
                  marginTop: spacing.sm,
                  backgroundColor: theme.colors.neutral[900],
                  color: theme.colors.neutral[50],
                  padding: spacing.md,
                  borderRadius: '4px',
                  overflow: 'auto',
                  fontSize: '13px'
                }}>
{`# Copy the example env and start the dev server
cp .env.example .env
npm run dev`}
                </pre>
              </div>

              {/* Reload Button */}
              <Button
                variant="solid"
                color="primary"
                onClick={() => window.location.reload()}
                style={{ marginTop: spacing.md }}
              >
                Reload Page
              </Button>
            </Stack>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}
